import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { getHealth, isApiConfigured } from "./apiClient";
import { isAdminHost } from "./lib/adminHost";
import AdminApp from "./AdminApp";
import PreviewPasswordGate from "./components/PreviewPasswordGate";
import LegalConsentGate from "./components/LegalConsentGate";
import VerificationGate from "./components/VerificationGate";
import Nd1LegacyRedirect from "./components/Nd1LegacyRedirect";
import HiringIsBrokenBlogPage from "./pages/HiringIsBrokenBlogPage";
import ApplyingForJobsIsBrokenBlogPage from "./pages/ApplyingForJobsIsBrokenBlogPage";
import IntroducingJobuwinBlogPage from "./pages/IntroducingJobuwinBlogPage";
import LegalDocumentPage from "./pages/LegalDocumentPage";
import Nd1HomePage from "./nd1/Nd1HomePage";
import Nd1HiringManagersPage from "./nd1/Nd1HiringManagersPage";
import Nd1LaunchFreePage from "./nd1/Nd1LaunchFreePage";
import Nd1BlogPage from "./nd1/Nd1BlogPage";
import Nd1JobSeekersPage from "./nd1/Nd1JobSeekersPage";
import Nd1HowItWorksApplyingPage from "./nd1/Nd1HowItWorksApplyingPage";
import Nd1HowItWorksHiringPage from "./nd1/Nd1HowItWorksHiringPage";
import Old1ArchiveLayout from "./old1/Old1ArchiveLayout";
import Old1HoldingPage from "./old1/pages/old1_HoldingPage";
import Old1HomePage from "./old1/pages/old1_HomePage";
import Old1BlogIndexPage from "./old1/pages/old1_BlogIndexPage";
import Old1SigninPage from "./old1/pages/old1_SigninPage";
import Old1SignupPage from "./old1/pages/old1_SignupPage";
import Old1VerifyEmailPage from "./old1/pages/old1_VerifyEmailPage";
import Old1VerifyMobilePage from "./old1/pages/old1_VerifyMobilePage";
import Old1ForgotPasswordPage from "./old1/pages/old1_ForgotPasswordPage";
import Old1ResetPasswordPage from "./old1/pages/old1_ResetPasswordPage";
import Old1ChangeEmailPage from "./old1/pages/old1_ChangeEmailPage";
import Old1ChangeMobilePage from "./old1/pages/old1_ChangeMobilePage";
import Old1LegalDocumentPage from "./old1/pages/old1_LegalDocumentPage";
import Old1HiringIsBrokenBlogPage from "./old1/pages/old1_HiringIsBrokenBlogPage";
import Old1ApplyingForJobsIsBrokenBlogPage from "./old1/pages/old1_ApplyingForJobsIsBrokenBlogPage";
import Old1IntroducingJobuwinBlogPage from "./old1/pages/old1_IntroducingJobuwinBlogPage";
import ApplicantHomePage from "./pages/ApplicantHomePage";
import ApplicantJobsListPage from "./pages/ApplicantJobsListPage";
import ApplicantSavedJobsPage from "./pages/ApplicantSavedJobsPage";
import ApplicantAppliedJobsPage from "./pages/ApplicantAppliedJobsPage";
import JobSearchPage from "./pages/JobSearchPage";
import SigninPage from "./pages/SigninPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ChangeEmailPage from "./pages/ChangeEmailPage";
import ChangeMobilePage from "./pages/ChangeMobilePage";
import ApplicantSearchPage from "./pages/ApplicantSearchPage";
import ApplicantProfilePage from "./pages/ApplicantProfilePage";
import JobDetailsPage from "./pages/JobDetailsPage";
import SignupPage from "./pages/SignupPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import VerifyMobilePage from "./pages/VerifyMobilePage";
import HiringDashboardPage from "./pages/HiringDashboardPage";
import CreateJobOpeningPage from "./pages/CreateJobOpeningPage";
import HiringRoleManagePage from "./pages/HiringRoleManagePage";
import RecruitmentProfilePage from "./pages/RecruitmentProfilePage";
import ResponseSettingsPage from "./pages/ResponseSettingsPage";
import RecruitmentPurchasesPage from "./pages/RecruitmentPurchasesPage";
import ResponseSettingsRejectionEditPage from "./pages/ResponseSettingsRejectionEditPage";
import ResponseSettingsSignatureEditPage from "./pages/ResponseSettingsSignatureEditPage";
import LegacyNotificationsRedirect from "./components/LegacyNotificationsRedirect";
import NotificationsPage from "./pages/NotificationsPage";
import RouteSeoFallback from "./components/RouteSeoFallback";
import { Nd1SessionProvider } from "./nd1/Nd1SessionContext";
import { TawkToUiProvider } from "./context/TawkToContext";
import TawkToWidget from "./components/TawkToWidget";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import CookieConsentBanner from "./components/CookieConsentBanner";
import NotFoundPage from "./pages/NotFoundPage";
import PostHogAnalytics from "./components/PostHogAnalytics";

function StaticPageRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function PublicApp() {
  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = window.localStorage.getItem("jobuwin-current-user");
    if (!savedUser) return null;
    try {
      return JSON.parse(savedUser);
    } catch {
      window.localStorage.removeItem("jobuwin-current-user");
      return null;
    }
  });

  const [authToken, setAuthToken] = useState(() => window.localStorage.getItem("jobuwin-auth-token"));

  useEffect(() => {
    if (currentUser) {
      window.localStorage.setItem("jobuwin-current-user", JSON.stringify(currentUser));
      return;
    }
    window.localStorage.removeItem("jobuwin-current-user");
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setAuthToken(null);
      window.localStorage.removeItem("jobuwin-auth-token");
      return;
    }
    if (authToken) {
      window.localStorage.setItem("jobuwin-auth-token", authToken);
    } else {
      window.localStorage.removeItem("jobuwin-auth-token");
    }
  }, [currentUser, authToken]);

  useEffect(() => {
    if (!isApiConfigured()) return;
    let cancelled = false;
    (async () => {
      try {
        const { ok } = await getHealth();
        if (!cancelled && ok && process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.info("[JOBUWIN] API health check OK");
        }
      } catch {
        if (!cancelled && process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("[JOBUWIN] API health check failed (is jobuwin-api running?)");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignIn = (user, meta) => {
    setCurrentUser(user);
    if (meta && Object.prototype.hasOwnProperty.call(meta, "token")) {
      if (meta.token) setAuthToken(meta.token);
      else setAuthToken(null);
    }
  };

  return (
    <Nd1SessionProvider
      currentUser={currentUser}
      setCurrentUser={setCurrentUser}
      authToken={authToken}
    >
    <CookieConsentProvider>
    <PostHogAnalytics currentUser={currentUser}>
    <TawkToUiProvider>
    <>
    <RouteSeoFallback />
    <Routes>
      <Route path="/" element={<Nd1HomePage />} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="/job-seekers" element={<Nd1JobSeekersPage />} />
      <Route path="/hiring-managers" element={<Nd1HiringManagersPage />} />
      {/* LAUNCH: Nd1LaunchFreePage on /pricing — restore Nd1PricingPage when charging starts */}
      <Route path="/pricing" element={<Nd1LaunchFreePage />} />
      <Route path="/how-it-works/applying" element={<Nd1HowItWorksApplyingPage />} />
      <Route path="/how-it-works/hiring" element={<Nd1HowItWorksHiringPage />} />
      <Route path="/blog" element={<Nd1BlogPage />} />
      <Route
        path="/blog/hiring-is-broken"
        element={<HiringIsBrokenBlogPage currentUser={currentUser} setCurrentUser={setCurrentUser} />}
      />
      <Route
        path="/blog/hiring-is-broken.html"
        element={<Navigate to="/blog/hiring-is-broken" replace />}
      />
      <Route
        path="/blog/applying-for-jobs-is-broken"
        element={
          <ApplyingForJobsIsBrokenBlogPage currentUser={currentUser} setCurrentUser={setCurrentUser} />
        }
      />
      <Route
        path="/blog/introducing-jobuwin"
        element={<IntroducingJobuwinBlogPage currentUser={currentUser} setCurrentUser={setCurrentUser} />}
      />
      <Route
        path="/signin"
        element={<SigninPage onSignIn={handleSignIn} currentUser={currentUser} />}
      />
      <Route
        path="/signup"
        element={
          <SignupPage onSignIn={handleSignIn} currentUser={currentUser} authToken={authToken} />
        }
      />
      <Route
        path="/verify-email"
        element={
          <VerifyEmailPage
            onSignIn={handleSignIn}
            authToken={authToken}
            currentUser={currentUser}
          />
        }
      />
      <Route
        path="/verify-mobile"
        element={
          <VerifyMobilePage
            onSignIn={handleSignIn}
            authToken={authToken}
            currentUser={currentUser}
          />
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/change-email"
        element={<ChangeEmailPage onSignIn={handleSignIn} authToken={authToken} />}
      />
      <Route path="/change-mobile" element={<ChangeMobilePage onSignIn={handleSignIn} />} />
      <Route
        path="/terms"
        element={
          <LegalDocumentPage docType="terms" currentUser={currentUser} setCurrentUser={setCurrentUser} />
        }
      />
      <Route
        path="/terms/:version"
        element={
          <LegalDocumentPage docType="terms" currentUser={currentUser} setCurrentUser={setCurrentUser} />
        }
      />
      <Route
        path="/privacy"
        element={
          <LegalDocumentPage docType="privacy" currentUser={currentUser} setCurrentUser={setCurrentUser} />
        }
      />
      <Route
        path="/privacy/:version"
        element={
          <LegalDocumentPage docType="privacy" currentUser={currentUser} setCurrentUser={setCurrentUser} />
        }
      />
      <Route
        path="/cookies"
        element={
          <LegalDocumentPage docType="cookies" currentUser={currentUser} setCurrentUser={setCurrentUser} />
        }
      />
      <Route
        path="/cookies/:version"
        element={
          <LegalDocumentPage docType="cookies" currentUser={currentUser} setCurrentUser={setCurrentUser} />
        }
      />
      <Route path="/nd1/*" element={<Nd1LegacyRedirect />} />
      <Route path="/coming-soon" element={<Navigate to="/old1/coming-soon" replace />} />
      <Route
        path="/jobs/:jobId"
        element={
          <JobDetailsPage
            currentUser={currentUser}
            setCurrentUser={setCurrentUser}
            authToken={authToken}
          />
        }
      />
      <Route element={<Old1ArchiveLayout />}>
        <Route path="/old1" element={<Navigate to="/old1/home" replace />} />
        <Route
          path="/old1/home"
          element={<Old1HomePage currentUser={currentUser} setCurrentUser={setCurrentUser} />}
        />
        <Route path="/old1/coming-soon" element={<Old1HoldingPage />} />
        <Route
          path="/old1/blog"
          element={<Old1BlogIndexPage currentUser={currentUser} setCurrentUser={setCurrentUser} />}
        />
        <Route
          path="/old1/blog/hiring-is-broken"
          element={
            <Old1HiringIsBrokenBlogPage currentUser={currentUser} setCurrentUser={setCurrentUser} />
          }
        />
        <Route
          path="/old1/blog/applying-for-jobs-is-broken"
          element={
            <Old1ApplyingForJobsIsBrokenBlogPage
              currentUser={currentUser}
              setCurrentUser={setCurrentUser}
            />
          }
        />
        <Route
          path="/old1/blog/introducing-jobuwin"
          element={
            <Old1IntroducingJobuwinBlogPage currentUser={currentUser} setCurrentUser={setCurrentUser} />
          }
        />
        <Route
          path="/old1/signin"
          element={<Old1SigninPage onSignIn={handleSignIn} currentUser={currentUser} />}
        />
        <Route
          path="/old1/signup"
          element={
            <Old1SignupPage onSignIn={handleSignIn} currentUser={currentUser} authToken={authToken} />
          }
        />
        <Route
          path="/old1/verify-email"
          element={<Old1VerifyEmailPage onSignIn={handleSignIn} authToken={authToken} />}
        />
        <Route
          path="/old1/verify-mobile"
          element={<Old1VerifyMobilePage onSignIn={handleSignIn} authToken={authToken} />}
        />
        <Route path="/old1/forgot-password" element={<Old1ForgotPasswordPage />} />
        <Route path="/old1/reset-password" element={<Old1ResetPasswordPage />} />
        <Route
          path="/old1/change-email"
          element={<Old1ChangeEmailPage onSignIn={handleSignIn} authToken={authToken} />}
        />
        <Route path="/old1/change-mobile" element={<Old1ChangeMobilePage onSignIn={handleSignIn} />} />
        <Route
          path="/old1/terms"
          element={
            <Old1LegalDocumentPage docType="terms" currentUser={currentUser} setCurrentUser={setCurrentUser} />
          }
        />
        <Route
          path="/old1/terms/:version"
          element={
            <Old1LegalDocumentPage docType="terms" currentUser={currentUser} setCurrentUser={setCurrentUser} />
          }
        />
        <Route
          path="/old1/privacy"
          element={
            <Old1LegalDocumentPage docType="privacy" currentUser={currentUser} setCurrentUser={setCurrentUser} />
          }
        />
        <Route
          path="/old1/privacy/:version"
          element={
            <Old1LegalDocumentPage docType="privacy" currentUser={currentUser} setCurrentUser={setCurrentUser} />
          }
        />
      </Route>
      <Route element={<PreviewPasswordGate />}>
        <Route
          path="/applicant-home"
          element={
            currentUser?.role === "applicant" ? (
              <ApplicantHomePage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/applicant-profile"
          element={
            currentUser?.role === "applicant" ? (
              <ApplicantProfilePage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/applicant/saved-jobs"
          element={
            currentUser?.role === "applicant" ? (
              <ApplicantSavedJobsPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/applicant/applied-jobs"
          element={
            currentUser?.role === "applicant" ? (
              <ApplicantAppliedJobsPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/notifications"
          element={
            currentUser ? (
              <LegacyNotificationsRedirect role={currentUser.role} />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/applicant/notifications"
          element={
            currentUser?.role === "applicant" ? (
              <NotificationsPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/hiring/notifications"
          element={
            currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter" ? (
              <NotificationsPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/hiring-dashboard"
          element={
            currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter" ? (
              <HiringDashboardPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/recruitment-profile"
          element={
            currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter" ? (
              <RecruitmentProfilePage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/purchases"
          element={
            currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter" ? (
              <RecruitmentPurchasesPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/response-settings"
          element={
            currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter" ? (
              <ResponseSettingsPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/response-settings/rejection/:templateId"
          element={
            currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter" ? (
              <ResponseSettingsRejectionEditPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/response-settings/signature"
          element={
            currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter" ? (
              <ResponseSettingsSignatureEditPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/hiring/jobs/new"
          element={
            currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter" ? (
              <CreateJobOpeningPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/hiring/roles/:roleId"
          element={
            currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter" ? (
              <HiringRoleManagePage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
        <Route
          path="/applicant-jobs/:listType"
          element={
            <ApplicantJobsListPage
              currentUser={currentUser}
              setCurrentUser={setCurrentUser}
              authToken={authToken}
            />
          }
        />
        <Route
          path="/job-search"
          element={
            <JobSearchPage currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken} />
          }
        />
        <Route
          path="/applicant-search/:searchId"
          element={
            currentUser?.role === "applicant" ? (
              <ApplicantSearchPage
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                authToken={authToken}
              />
            ) : (
              <Navigate to="/signin" replace />
            )
          }
        />
      </Route>
      <Route path="/design" element={<StaticPageRedirect to="/Design/main-style.html" />} />
      <Route path="/design/main-style" element={<StaticPageRedirect to="/Design/main-style.html" />} />
      <Route path="/design/applicant-style" element={<StaticPageRedirect to="/Design/applicant-style.html" />} />
      <Route path="/design/hiring-style" element={<StaticPageRedirect to="/Design/hiring-style.html" />} />
      <Route path="/admin" element={<Navigate to="/" replace />} />
      <Route path="/admin/*" element={<Navigate to="/" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    <VerificationGate
      currentUser={currentUser}
      setCurrentUser={setCurrentUser}
      authToken={authToken}
    />
    <LegalConsentGate
      currentUser={currentUser}
      setCurrentUser={setCurrentUser}
      authToken={authToken}
    />
    <TawkToWidget currentUser={currentUser} />
    <CookieConsentBanner />
    </>
    </TawkToUiProvider>
    </PostHogAnalytics>
    </CookieConsentProvider>
    </Nd1SessionProvider>
  );
}

function App() {
  if (isAdminHost()) {
    return <AdminApp />;
  }
  return <PublicApp />;
}

export default App;
